import type { Sufficiency, WeightTrend } from '~/calculations/weightTrends'
import type { WeightDisplayUnit } from '~/models/settings'
import { formatPercentChange, formatWeight, formatWeightChange } from '~/utils/format'
import styles from './TrendSummary.module.css'

/**
 * Seven-day trend display (D-036).
 *
 * Two rules drive everything here:
 *
 *   - an insufficient window shows the entry count and NO average, because a
 *     mean of one weigh-in is not a trend;
 *   - the week-on-week comparison appears only when the calculation supplied
 *     one, which happens only when both windows are sufficient.
 *
 * Data quality is stated in words, never implied by a colour or an icon alone.
 */

/**
 * **`detailed` was removed at C7.4 phase C, once Today stopped being a caller.**
 * Today now renders a fixed-height `StatusCard` instead, so the flag had exactly
 * one caller passing exactly one value — an option nothing chooses between is a
 * branch that can only rot. D-104 §8: obsolete patterns go once no caller
 * remains.
 */
interface TrendSummaryProps {
  trend: WeightTrend
  unit: WeightDisplayUnit
}

const SUFFICIENCY_NOTE: Readonly<Record<Sufficiency, string>> = {
  sufficient: '',
  limited: 'Based on limited data this week.',
  insufficient: 'Not enough weigh-ins yet to show an average.',
}

function weighInCount(count: number): string {
  return `${count} weigh-in${count === 1 ? '' : 's'}`
}

export function TrendSummary({ trend, unit }: TrendSummaryProps) {
  const { current, previous, comparison } = trend

  return (
    <section class={styles.card} aria-labelledby="trend-heading">
      <h2 id="trend-heading" class={styles.heading}>
        Seven-day average
      </h2>

      {current.averageKg === undefined ? (
        <p class={styles.empty}>
          {SUFFICIENCY_NOTE.insufficient} {weighInCount(current.entryCount)} in the last seven days.
        </p>
      ) : (
        <>
          <p class={styles.value}>{formatWeight(current.averageKg, unit)}</p>
          <p class={styles.meta}>
            {weighInCount(current.entryCount)} in the last seven days.
            {current.sufficiency === 'limited' ? ` ${SUFFICIENCY_NOTE.limited}` : ''}
          </p>
        </>
      )}

      {comparison === undefined ? (
        current.averageKg === undefined ? null : (
          <p class={styles.meta}>Not enough data to compare with the previous seven days yet.</p>
        )
      ) : (
        <p class={styles.change}>
          <span class={styles.changeValue}>{formatWeightChange(comparison.changeKg, unit)}</span>{' '}
          <span class={styles.changeMeta}>
            ({formatPercentChange(comparison.percentChange)}) versus the previous seven days
          </span>
        </p>
      )}

      <dl class={styles.detail}>
        <div class={styles.detailRow}>
          <dt>This week</dt>
          <dd>
            {current.averageKg === undefined ? '—' : formatWeight(current.averageKg, unit)}
            <span class={styles.detailNote}> · {weighInCount(current.entryCount)}</span>
          </dd>
        </div>
        <div class={styles.detailRow}>
          <dt>Previous week</dt>
          <dd>
            {previous.averageKg === undefined ? '—' : formatWeight(previous.averageKg, unit)}
            <span class={styles.detailNote}> · {weighInCount(previous.entryCount)}</span>
          </dd>
        </div>
        <div class={styles.detailRow}>
          <dt>Window</dt>
          <dd class={styles.detailNote}>
            {current.window.from} to {current.window.to}
          </dd>
        </div>
      </dl>
    </section>
  )
}
